import ReactDOM from 'react-dom/client';
import MessageModal, { MessageType } from './MessageModal';

// A map to store our message IDs and their timeouts
const messageMap = new Map<
	string,
	{
		timeoutId: NodeJS.Timeout;
		element: HTMLDivElement;
		close: () => void;
		root: ReactDOM.Root;
	}
>();

// Interface for message options
interface MessageOptions {
	title?: string;
	autoClose?: number;
}

// Create a custom centered message modal
const showCenteredMessage = (message: string, type: MessageType, options: MessageOptions = {}): string => {
	const { title, autoClose = 5000 } = options;

	// Create a unique ID for this message
	const messageId = `message-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

	// Create a container for the message modal root element
	const modalRoot = document.createElement('div');
	modalRoot.id = messageId;
	document.body.appendChild(modalRoot);

	const root = ReactDOM.createRoot(modalRoot);

	// Track whether the modal is open
	let isOpen = true;

	// Close function for the modal
	const closeModal = () => {
		isOpen = false;
		const info = messageMap.get(messageId);

		// Clear any existing timeout
		if (info?.timeoutId) {
			clearTimeout(info.timeoutId);
		}

		// Render with open=false to trigger the fade-out animation
		renderModal();

		// Remove after animation completes
		setTimeout(() => {
			// Use root.unmount() instead of ReactDOM.unmountComponentAtNode
			info?.root.unmount();
			if (document.body.contains(modalRoot)) {
				document.body.removeChild(modalRoot);
			}
			messageMap.delete(messageId);
		}, 300);
	};

	// Create timeout for auto-close if specified
	let timeoutId: NodeJS.Timeout | undefined;
	if (autoClose > 0) {
		timeoutId = setTimeout(closeModal, autoClose);
	}

	// Store in our map
	messageMap.set(messageId, {
		timeoutId: timeoutId as NodeJS.Timeout,
		element: modalRoot,
		close: closeModal,
		root,
	});

	// Function to render the modal
	const renderModal = () => {
		root.render(<MessageModal message={message} type={type} closeModal={closeModal} open={isOpen} title={title} />);
	};

	// Initial render
	renderModal();

	// Return the ID so it can be dismissed programmatically
	return messageId;
};

// Function to dismiss a specific message modal
export const dismissMessage = (messageId: string): void => {
	const info = messageMap.get(messageId);
	if (info) {
		info.close();
	}
};

// Function to dismiss all message modals
export const dismissAllMessages = (): void => {
	messageMap.forEach(({ close }) => {
		close();
	});
};

// Export convenience methods with centered positioning
export const showSuccessMessage = (message: string, options?: MessageOptions): string =>
	showCenteredMessage(message, 'success', options);

export const showErrorMessage = (message: string, options?: MessageOptions): string =>
	showCenteredMessage(message, 'error', options);

export const showInfoMessage = (message: string, options?: MessageOptions): string =>
	showCenteredMessage(message, 'info', options);

export const showWarningMessage = (message: string, options?: MessageOptions): string =>
	showCenteredMessage(message, 'warning', options);

// General message function that accepts a type parameter
export const showMessage = (message: string, type: MessageType = 'info', options?: MessageOptions): string =>
	showCenteredMessage(message, type, options);
